import zlFetch from "zl-fetch"
import humps from "humps"
import {
  fixDRFErrorBody,
  isDRFErrorBody,
} from "./djangoRestFrameworkUtils"

const methods = [
  "get",
  "post",
  "put",
  "patch",
  "delete",
] as const

interface ApiOptions {
  headers?: HeadersInit
  body?: object | string | undefined | FormData
  auth?: string
  debug?: boolean
  returnError?: boolean
  customResponseParser?: boolean
  query?: object
}

export type ApiResponse<T> = {
  headers: Headers
  body: T
  status: number
  statusText: string
  response: Response
}

type RequestDataSource =
  | "path"
  | "query"
  | "header"
  | "cookie"
  | "body"
  | "form"
  | "file"

type RequestSourceErrorMap = {
  [location: string]: string
}

type NinjaErrorBody = {
  detail: string
  code?: string
  requestErrors?: Partial<
    Record<RequestDataSource, RequestSourceErrorMap>
  >
  otherErrors?: any
}

export class ApiError extends Error {
  status: number
  detail?: string
  code?: string
  requestErrors?: Partial<
    Record<RequestDataSource, RequestSourceErrorMap>
  >
  otherErrors?: any

  constructor(response: ApiResponse<unknown>) {
    super()

    this.status = response.status

    if (isNinjaErrorBody(response.body)) {
      this.detail = response.body.detail
      this.code = response.body.code
      this.requestErrors = response.body.requestErrors
      this.otherErrors = response.body.otherErrors
    } else if (isDRFErrorBody(response.body)) {
      this.requestErrors = {
        body: fixDRFErrorBody(response.body),
      }
    }
  }
}

type ApiMethod = <T>(
  url: string,
  options?: ApiOptions
) => Promise<ApiResponse<T>>

type Api = Record<(typeof methods)[number], ApiMethod>

const buildUrl = (path: string) => `${__API_ROOT__}${path}`
export const api = {} as Api

for (const method of methods) {
  api[method] = async (
    path: string,
    { query, ...options }: ApiOptions = {}
  ) => {
    // Putting this is for legacy API, but might be good to do this all the time
    if (
      query &&
      "ordering" in query &&
      typeof query.ordering === "string"
    ) {
      query.ordering = humps.decamelize(query.ordering)
    }

    const url = query
      ? buildUrl(`${path}${buildQueryString(query)}`)
      : buildUrl(path)

    const maybeCsrfCookie =
      document.cookie
        .split(";")
        .find((row) => row.trim().startsWith("csrftoken="))
        ?.split("=")[1] || undefined

    try {
      const response = await zlFetch(url, {
        ...options,
        body: options.body
          ? humps.decamelizeKeys(options.body)
          : undefined,
        method: method.toUpperCase(),
        headers: {
          ...(maybeCsrfCookie && {
            "X-CSRFTOKEN": maybeCsrfCookie,
          }),
          ...options.headers,
        },
      })

      return {
        ...response,
        body: response.body
          ? humps.camelizeKeys(response.body)
          : undefined,
      }
    } catch (error_) {
      const error = error_ as ApiResponse<unknown>

      throw new ApiError(error)
    }
  }
}

const isNinjaErrorBody = (
  body: unknown
): body is NinjaErrorBody => {
  return (
    typeof body === "object" &&
    body !== null &&
    "detail" in body
  )
}
